import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ShoppingBag, Phone } from 'lucide-react';
import logoSquareImg from '../assets/logo-haosong.jpg';
import LanguageSwitcher from './LanguageSwitcher';

interface SiteHeaderProps {
  cartCount?: number;
  onCartClick?: () => void;
  rightExtra?: React.ReactNode;
  hideNav?: boolean;
}

const SiteHeader: React.FC<SiteHeaderProps> = ({ cartCount = 0, onCartClick, rightExtra, hideNav = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isHome = location.pathname === '/' || location.pathname.startsWith('/campaign/');
  const isMyPage = location.pathname.startsWith('/my/');
  const shouldHideNav = hideNav || isMyPage;

  interface NavItem { label: string; href?: string; action?: () => void; external?: string; }
  const navItems: NavItem[] = [
    { label: t('common:nav.home'), href: '/' },
    { label: t('common:nav.about'), href: '/about' },
    { label: t('common:nav.media'), href: '/about#media-coverage' },
    {
      label: t('common:nav.featured'),
      action: () => {
        if (isHome) {
          document.getElementById('shop')?.scrollIntoView({ behavior: 'smooth' });
        } else {
          navigate('/');
        }
        setMobileOpen(false);
      },
    },
    // { label: '招募義工', href: '/volunteer' },
    // 機構合作（/collab/）暫時隱藏：待 STC 細節正式確認後再上架（頁面本身保持直連可達）
    // { label: '機構合作', external: '/collab/' },
    { label: t('common:nav.contact'), href: '/about#contact' },
  ];

  const handleHref = (href: string) => {
    setMobileOpen(false);
    if (href.includes('#')) {
      const [path, hash] = href.split('#');
      navigate(path);
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      navigate(href);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-stone-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-[3px] flex items-center justify-between">
          {/* Logo — 統一使用 PC 版佈局：左方形 logo + 右品牌標語 */}
          <button
            onClick={() => handleHref('/')}
            className="flex items-center gap-3 text-left"
          >
            <img
              src={logoSquareImg}
              alt={t('common:brand.logoAlt')}
              className="h-12 w-auto rounded-lg shadow-sm"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <div>
              <span className="text-lg md:text-xl font-bold text-[#C2410C] tracking-wide">
                {t('common:nav.brandName')}
              </span>
              <p className="text-[10px] md:text-xs text-stone-500 tracking-wider">
                {t('common:nav.brandTagline')}
              </p>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {!shouldHideNav &&
              navItems.map((item) =>
                item.external ? (
                  <a
                    key={item.label}
                    href={item.external}
                    className="text-sm font-medium text-stone-600 hover:text-[#C2410C] transition-colors inline-flex items-center"
                  >
                    {item.label}
                  </a>
                ) : item.href ? (
                  <button
                    key={item.label}
                    onClick={() => handleHref(item.href!)}
                    className="text-sm font-medium text-stone-600 hover:text-[#C2410C] transition-colors"
                  >
                    {item.label}
                  </button>
                ) : (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className="text-sm font-medium text-stone-600 hover:text-[#C2410C] transition-colors"
                  >
                    {item.label}
                  </button>
                )
              )}

            <LanguageSwitcher />

            {cartCount > 0 && onCartClick && (
              <button
                onClick={onCartClick}
                className="flex items-center gap-2 bg-[#C2410C] text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-[#9a3412] transition-colors shadow-sm"
              >
                <ShoppingBag size={16} />
                <span>{cartCount}</span>
              </button>
            )}

            {rightExtra}
          </nav>

          {/* Mobile actions */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher size="sm" />
            {cartCount > 0 && onCartClick && (
              <button
                onClick={onCartClick}
                className="flex items-center gap-1.5 bg-[#C2410C] text-white px-3 py-1.5 rounded-full text-sm font-bold"
              >
                <ShoppingBag size={14} />
                <span>{cartCount}</span>
              </button>
            )}
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 text-stone-600"
              aria-label={t('common:nav.openMenu')}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay — rendered as a sibling of header to avoid stacking context issues */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-[55] md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="fixed top-0 right-0 h-auto max-h-screen w-60 bg-white shadow-2xl z-[60] md:hidden flex flex-col rounded-bl-2xl overflow-hidden"
            style={{ backgroundColor: '#ffffff' }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-white">
              <span className="font-bold text-[#C2410C]">{t('common:nav.menu')}</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 text-stone-600"
                aria-label={t('common:nav.closeMenu')}
              >
                <X size={24} />
              </button>
            </div>
            <nav className="px-4 py-4 space-y-1 bg-white">
              {!shouldHideNav &&
                navItems.map((item) =>
                  item.external ? (
                    <a
                      key={item.label}
                      href={item.external}
                      className="block w-full text-left px-3 py-2.5 rounded-lg text-stone-700 hover:bg-[#FFF7ED] transition-colors bg-white whitespace-nowrap"
                    >
                      {item.label}
                    </a>
                  ) : item.href ? (
                    <button
                      key={item.label}
                      onClick={() => handleHref(item.href!)}
                      className="w-full text-left px-3 py-2.5 rounded-lg text-stone-700 hover:bg-[#FFF7ED] transition-colors bg-white whitespace-nowrap"
                    >
                      {item.label}
                    </button>
                  ) : (
                    <button
                      key={item.label}
                      onClick={item.action}
                      className="w-full text-left px-3 py-2.5 rounded-lg text-stone-700 hover:bg-[#FFF7ED] transition-colors bg-white whitespace-nowrap"
                    >
                      {item.label}
                    </button>
                  )
                )}
            </nav>
            <div className="p-4 border-t border-stone-100 space-y-3 bg-white">
              <div className="flex justify-center">
                <LanguageSwitcher />
              </div>
              <a
                href="https://wa.me/85257961378"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white font-bold py-2.5 rounded-xl text-sm"
              >
                <Phone size={16} />
                {t('common:nav.whatsapp')}
              </a>
              {rightExtra && <div className="flex justify-center">{rightExtra}</div>}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SiteHeader;
